/* eslint-disable max-lines */
import _ from 'lodash';
import { batch } from 'react-redux';
// ---- external imports -----

import Sound from '~/lib/Sound';
import chatterSoundUrl from '~/assets/images/customers/chatter.mp3';
import sweepSoundUrl from '~/assets/images/customers/sweep.mp3';
import stepSoundUrl from '~/assets/images/customers/steps.mp3';
import sellCoinsSoundUrl from '~/assets/images/customers/coins.mp3';
import salesmanSellingSoundUrl from '~/assets/sounds/employees/salesmanSelling.mp3';
import GameService from '~/services/game.service';
import { setLocalStore } from '~/lib/helpers';
import {
	CUSTOMER_ANIMATION_STATE,
	CUSTOMER_REPLACEMENT_DELAY_MS,
	CUSTOMER_TOTAL_ANIMATION_DURATION_MS,
	FIRST_CUSTOMER_DELAY_MS
} from '~/containers/game-user/customers-wrapper/animationConstants';
import { JOB_TYPES, MAX_CUSTOMERS_IN_VIEW, CUSTOMER_TYPES } from '~/lib/constants';
import { getActinTypeForSellProduct } from '../products/actions';
import { shouldSkipCustomerAnimations } from '~/lib/helpers';
// ---- internal imports -----

import { productsActions, employeesActions, uiActions, employeesSelectors, productsSelectors, gameActions, gameSelectors, inboxActions, turnSelectors } from '../';
import { UI_UPDATE_HAS_PENDING_CUSTOMER_TYPE_HINTS } from '../ui/actionTypes';
import { PRODUCTS_SOLD } from '../products/actionTypes';
// ---- parent imports -----

import * as types from './actionTypes';
import * as customerSelectors from './selectors';
import {
	getSalesFromValidCustomers,
	addAnimationOrder,
	createCustomersModels
} from './utlis';
import { getLevelNumber } from '../level/selectors';
import { getTurnNum } from '../turn/selectors';
// ---- local imports -----

const MINIMUM_CUSTOMER_COUNT_FOR_CHATTER_SOUND = 2;
const CHATTER_SOUND_FADE_TIME = 1000;
const salesmanSellingSound = new Sound(salesmanSellingSoundUrl);
const stepSound = new Sound(stepSoundUrl);
const sweepSound = new Sound(sweepSoundUrl);
const sellCoinsSound = new Sound(sellCoinsSoundUrl);
const chatterSound = new Sound(chatterSoundUrl, 1, true, CHATTER_SOUND_FADE_TIME);
const customerTimers = new Array(MAX_CUSTOMERS_IN_VIEW).fill(null);
let extraCustomerButtonTimer = null;


function setNumberOfVisibleCustomers(customersAnimatedVisibleCount) {
	if (customersAnimatedVisibleCount >= MINIMUM_CUSTOMER_COUNT_FOR_CHATTER_SOUND) {
		chatterSound.play();
	}
	else {
		chatterSound.pause();
	}

	return {
		type: types.CUSTOMERS_SET_ANIMATED_VISIBLE_CUSTOMER_COUNT,
		customersAnimatedVisibleCount
	};
}

function animateCustomerAsVisible(customerIndexToMakeVisible) {
	return async (dispatch, getState) => {
		stepSound.play();

		dispatch(incrementNumberOfVisibleCustomers());

		const currentViewingCustomers = customerSelectors.customersViewing(getState());
		const updatedViewingCustomers = currentViewingCustomers.map((viewedCustomer) => {
			if (!viewedCustomer || viewedCustomer.id != customerIndexToMakeVisible) {
				return viewedCustomer;
			}

			return {
				...viewedCustomer,
				animationState: CUSTOMER_ANIMATION_STATE.IDLE
			};
		});

		dispatch({
			type: types.CUSTOMERS_UPDATE_VIEWING_CUSTOMERS,
			viewing: updatedViewingCustomers
		});
	};
}

function prepareCustomerAnimation(hasProductsInStock) {
	return async (dispatch, getState) => {
		if (!hasProductsInStock) {
			return;
		}
		const shouldSkip = shouldSkipCustomerAnimations();

		customerSelectors.customersViewing(getState())
			.forEach((customer, index) => {
				if (!customer) {
					return;
				}
				
				const animateStartWaitTime = shouldSkip 
					? 0 
					: FIRST_CUSTOMER_DELAY_MS +
					(CUSTOMER_TOTAL_ANIMATION_DURATION_MS * customer.animationOrderIndex);

				customerTimers[index] = setTimeout(
					() => dispatch(animateCustomerAsVisible(customer.id)),
					animateStartWaitTime
				);
			});
	};
}


export function receivedTurnCustomers(customers) {
	return async (dispatch, getState) => {
		const turnCustomers = createCustomersModels(customers);
		dispatch({ type: types.CUSTOMERS_RECEIVED_TURN_CUSTOMERS, turnCustomers });
	};
}

export function prepareCustomers() {
	return async (dispatch, getState) => {
		dispatch(enableNegotiator(false));

		const turnCustomers = customerSelectors.getTurnCustomers(getState());
		const hasProductsInStock = productsSelectors.hasProductsInStock(getState());

		const customers = turnCustomers
			.map((customer, id) => {
				return { ...customer, key: id };
			});

		const viewing =
			addAnimationOrder(customers.slice(0, MAX_CUSTOMERS_IN_VIEW), hasProductsInStock);
		const waiting = customers.slice(MAX_CUSTOMERS_IN_VIEW, customers.length);
		const validViewingCustomers =
			customerSelectors.getValidCustomersFromViewing(getState(), viewing);
		const salesCustomers = getSalesFromValidCustomers(getState(), validViewingCustomers);

		const validViewingCustomersCount = validViewingCustomers.length;
		const invalidViewingCustomersCount = viewing.length - salesCustomers.length;
		const canNegotiateCustomers =
			customerSelectors.canNegotiateCustomers(getState(), viewing);
		const pendingBargainingCount =
			viewing.filter((cust) => cust.type === CUSTOMER_TYPES.BARGAINING).length;

		
		//	level 1 has 2 customer type tutorials
		//	one in first turn, other in second turn
		const isFirstLevel = getLevelNumber(getState()) === 1;
		const isFirstTurn = getTurnNum(getState()) === 1;
		const hasPendingCustTutorialNextTurn = isFirstLevel && isFirstTurn;

		batch(() => {
			dispatch({ type: types.CUSTOMERS_RESET_SALES_FROM_VIEW_COUNT });
			dispatch({
				type: types.CUSTOMERS_PREPARE, waiting, viewing, numDismissed: 0,
				validViewingCustomers, validViewingCustomersCount, invalidViewingCustomersCount,
				pendingBargainingCount
			});

			
			dispatch({
				type: UI_UPDATE_HAS_PENDING_CUSTOMER_TYPE_HINTS,
				hasPendingCustomerTypeHint: hasPendingCustTutorialNextTurn
			});
		});


		if (canNegotiateCustomers) {
			dispatch(enableNegotiator(true));
		}
		else {
			dispatch(employeesActions.updateEmployeeWorkStatusDone(JOB_TYPES.NEGOTIATOR));
		}
		dispatch(prepareCustomerAnimation(hasProductsInStock));
	};
}

export function loadCustomers(loadedCustomers) {
	return async (dispatch, getState) => {
		dispatch(enableNegotiator(false));

		const remainingCustomers = loadedCustomers.filter((customer) => customer.state === 'waiting');
		const turnCustomers = createCustomersModels(remainingCustomers);
		const dismissedCustomersCount = loadedCustomers.filter((customer) => customer.state === 'dismissed');
		const soldCustomersCount = loadedCustomers.filter((customer) => customer.state === 'sold').length;

		const hasProductsInStock = productsSelectors.hasProductsInStock(getState());

		const customers = turnCustomers
			.map((customer, id) => {
				return { ...customer, key: id };
			});

		const viewing =
			addAnimationOrder(customers.slice(0, MAX_CUSTOMERS_IN_VIEW), hasProductsInStock);
		const waiting = customers.slice(MAX_CUSTOMERS_IN_VIEW, customers.length);
		const validViewingCustomers =
			customerSelectors.getValidCustomersFromViewing(getState(), viewing);
		const salesCustomers = getSalesFromValidCustomers(getState(), validViewingCustomers);

		const validViewingCustomersCount = validViewingCustomers.length;
		const invalidViewingCustomersCount = viewing.length - salesCustomers.length;
		const canNegotiateCustomers =
			customerSelectors.canNegotiateCustomers(getState(), viewing);
		const pendingBargainingCount =
			viewing.filter((cust) => cust.type === CUSTOMER_TYPES.BARGAINING).length;
		batch(() => {
			dispatch({ type: types.CUSTOMERS_RESET_SALES_FROM_VIEW_COUNT });
			dispatch({
				type: types.CUSTOMERS_PREPARE, waiting, viewing, numDismissed: dismissedCustomersCount,
				validViewingCustomers, validViewingCustomersCount, invalidViewingCustomersCount,
				pendingBargainingCount, soldCustomersCount
			});
			dispatch({
				type: UI_UPDATE_HAS_PENDING_CUSTOMER_TYPE_HINTS,
				hasPendingCustomerTypeHint: false
			});
		});


		if (canNegotiateCustomers) {
			dispatch(enableNegotiator(true));
		}
		else {
			dispatch(employeesActions.updateEmployeeWorkStatusDone(JOB_TYPES.NEGOTIATOR));
		}

		if (validViewingCustomers.length === 0) {
			dispatch(employeesActions.updateEmployeeWorkStatusDone(JOB_TYPES.SALESMAN));
		}

		dispatch(prepareCustomerAnimation(hasProductsInStock));
	};
}

export function skipCurrentCustomers() {
	return async (dispatch) => {
		customerTimers.forEach((timer) => {
			clearTimeout(timer);
		});
		clearTimeout(extraCustomerButtonTimer);

		dispatch({ type: types.CUSTOMERS_FINISH_TURN, waiting: [], viewing: [], numDismissed: 0 });
		dispatch(setNumberOfVisibleCustomers(0));
	};
}

// scenario 2 (if applicable)
export function requestMoreCustomers() {
	return async (dispatch, getState) => {
		customerTimers.forEach((timer) => {
			clearTimeout(timer);
		});

		await dispatch(employeesActions.resetCustomerEmployeesWorkCycle());
		dispatch({ type: types.CUSTOMERS_RESET_SALES_FROM_VIEW_COUNT });
		dispatch({ type: types.CUSTOMERS_ENTRY_ANIMATION_COMPLETE });
		const currentViewing = customerSelectors.customersViewing(getState()).asMutable();
		const numDismissed = customerSelectors.getNumDismissed(getState());
		const currentVisibleCustomerCount = currentViewing.length - numDismissed;

		if (currentVisibleCustomerCount === 0) {
			dispatch(replaceAllCustomers());
			return;
		}

		sweepSound.play();
		const updatedViewingCustomers = currentViewing.map((viewingCustomer) => {
			return {
				...viewingCustomer,
				animationState: CUSTOMER_ANIMATION_STATE.CLEARED
			};
		});

		dispatch({
			type: types.CUSTOMERS_REQUEST_MORE,
			viewing: updatedViewingCustomers
		});

		const shouldSkip = shouldSkipCustomerAnimations();
		const replacementDelay = shouldSkip ? 0 : CUSTOMER_REPLACEMENT_DELAY_MS;

		setTimeout(() => dispatch(replaceAllCustomers()), replacementDelay);
	};
}

export function replaceAllCustomers() {
	return async (dispatch, getState) => {
		let waiting = customerSelectors.customersWaiting(getState()).asMutable();
		const hasProductsInStock = productsSelectors.hasProductsInStock(getState());

		const viewingCustomers = new Array(MAX_CUSTOMERS_IN_VIEW).fill(null).map(() => {
			if (waiting.length === 0) {
				return null;
			}
			return waiting.shift();
		});

		const viewingCustomersWithAnimationData =
			addAnimationOrder(viewingCustomers, hasProductsInStock);

		const validViewingCustomers = customerSelectors
			.getValidCustomersFromViewing(getState(), viewingCustomersWithAnimationData);

		const validViewingCustomersCount = validViewingCustomers?.length;
		const salesCustomers = getSalesFromValidCustomers(getState(), validViewingCustomers);
		const invalidViewingCustomersCount = viewingCustomersWithAnimationData
			.filter((cust) => cust !== null).length - salesCustomers.length;

		const canNegotiateCustomers =
			customerSelectors.canNegotiateCustomers(getState(), viewingCustomersWithAnimationData);
		const pendingBargainingCount = viewingCustomersWithAnimationData
			.filter((cust) => cust?.type === CUSTOMER_TYPES.BARGAINING).length;

		dispatch(setNumberOfVisibleCustomers(0));
		dispatch({
			type: types.CUSTOMERS_PROVIDE_MORE, waiting,
			viewing: viewingCustomersWithAnimationData, numDismissed: 0,
			validViewingCustomers, validViewingCustomersCount,
			invalidViewingCustomersCount,
			pendingBargainingCount
		});

		if (waiting.length === 0 && validViewingCustomersCount === 0) {
			dispatch(uiActions.notifyAllCustomersAreHandled());
		}

		if (canNegotiateCustomers) {
			dispatch(enableNegotiator(true));
		}
		else {
			dispatch(enableNegotiator(false));
			dispatch(employeesActions.updateEmployeeWorkStatusDone(JOB_TYPES.NEGOTIATOR));
		}

		dispatch(prepareCustomerAnimation(hasProductsInStock));
	};
}

export function decrementPendingBargaining() {

	return {
		type: types.CUSTOMERS_DECREMENT_PENDING_BARGAINING_COUNT,
	};
}

export function dismissCustomer(customer, hasBoughtProduct = true) {
	return async (dispatch, getState) => {

		const hasHiredSalesman = employeesSelectors.hasSalesman(getState());
		let animationState;

		const prevViewed = customerSelectors.customersViewing(getState());
		const customerIndex = prevViewed.indexOf(customer);

		if (hasBoughtProduct) {
			dispatch(productsActions.sellProduct(customer, customerIndex));
			if (hasHiredSalesman) {
				salesmanSellingSound.play();
				animationState = CUSTOMER_ANIMATION_STATE.SOLD_SALESMAN;
			}
			else {
				sellCoinsSound.play();
				animationState = CUSTOMER_ANIMATION_STATE.SOLD;
			}
		}
		else {
			animationState = CUSTOMER_ANIMATION_STATE.DISMISSED;
		}


		const viewing = prevViewed.map((viewedCustomer) => {
			if (viewedCustomer === null || viewedCustomer.id != customer.id) {
				return viewedCustomer;
			}
			return {
				...viewedCustomer,
				animationState,
			};
		});

		const numDismissed = customerSelectors.getNumDismissed(getState()) + 1;
		let viewingCustomersSalesCount = customerSelectors
			.getViewingCustomersSalesCount(getState());

		viewingCustomersSalesCount = hasBoughtProduct
			? viewingCustomersSalesCount + 1
			: viewingCustomersSalesCount;

		let validViewingCustomers = customerSelectors
			.getValidCustomersFromViewing(getState(), viewing);

		const customersAnimatedVisibleCount = customerSelectors
			.getCustomersAnimatedVisibleCount(getState());

		batch(() => {
			if (hasBoughtProduct) {
				dispatch({ type: types.CUSTOMERS_SELL_TO_CUSTOMER, customer, customerIndex });
			}

			dispatch(setNumberOfVisibleCustomers(customersAnimatedVisibleCount - 1));
			dispatch({
				type: types.CUSTOMERS_DISMISS,
				viewing,
				numDismissed,
				viewingCustomersSalesCount,
				validViewingCustomers
			});

			if (validViewingCustomers.length === 0) {
				const hasExtraCustomers = customerSelectors.hasExtraCustomers(getState());

				dispatch({
					type: types.CUSTOMERS_RESET_VALID_VIEWING,
				});

				if (hasHiredSalesman) {
					setTimeout(() => {
						dispatch(employeesActions.updateEmployeeWorkStatusDone(JOB_TYPES.SALESMAN));
					}, 1000);
				}

				if (!hasExtraCustomers && !validViewingCustomers.length) {
					dispatch(uiActions.notifyAllCustomersAreHandled());
				}
			}
		});
	};
}

export function sellToAllValidViewingCustomers() {
	return async (dispatch, getState) => {
		const viewingCustomers = customerSelectors.customersViewing(getState());
		const validViewingCustomers = customerSelectors
			.getValidCustomersFromViewing(getState(), viewingCustomers);
		const salesCustomers = getSalesFromValidCustomers(getState(), validViewingCustomers);

		salesCustomers.forEach((customer, index) => {
			setTimeout(() => {
				dispatch(dismissCustomer(customer));
			}, index * 2000);
		});
	};
}

export function enableNegotiator(enable) {
	return {
		type: types.CUSTOMERS_ENABLE_NEGOTIATOR_FOR_VIEWING_CUSTOMERS,
		enable
	};
}

export function incrementNumberOfVisibleCustomers() {
	return async (dispatch, getState) => {
		const customersAnimatedVisibleCount = customerSelectors
			.getCustomersAnimatedVisibleCount(getState());
		dispatch(setNumberOfVisibleCustomers(customersAnimatedVisibleCount + 1));
	};
}

export function onCustomerEntryAnimationComplete() {
	return async (dispatch, getState) => {
		const viewingCustomers = customerSelectors.customersViewing(getState());
		const entryAnimationsCompleteCount =
			customerSelectors.getEntryAnimationCompleteCount(getState()) + 1;
		const viewingCount = viewingCustomers.filter((cust) => cust !== null).length;

		if (viewingCount === entryAnimationsCompleteCount) {
			dispatch(employeesActions.updateEmployeesCanStartWorkWithViewingCustomers(true));
			dispatch({ type: types.CUSTOMERS_ENTRY_ANIMATION_COMPLETE });
		}
		else {
			dispatch({ type: types.CUSTOMERS_INCREMENT_ENTRY_ANIMATION_COMPLETE_COUNT });
		}
	};
}

export function sellToTradeshowCustomer(customer) {
	return async (dispatch, getState) => {
			// dispatch(productsActions.sellProduct(tradeshowCustomer, tradeshowCustomer?.avatarIndex, true));

		let { productName, type } = customer;
		let discount = 0, productCount = 1;
		const customerIndex = customer.avatarIndex;
		discount =  customer.discount;
		productCount = customer.productCount;
		productName = customer.productType;


		const product = productsSelectors.getProduct(getState(), productName);
		if(!product) {
			throw new Error(`unhandled product type:${productName}`);
		}

		const { amount: productAmountInStock, sellingPrice } = product;

		if(productAmountInStock < productCount) {
			console.warn(`Not enough product amount for product:${productName}`);
			return;
		}

		const sellingPriceAfterDiscount = sellingPrice - (sellingPrice * discount / 100);

		const actionType = getActinTypeForSellProduct(productName);
		dispatch({ type: actionType, amount: productAmountInStock - productCount });

		const amountToAddToCash = sellingPriceAfterDiscount * productCount;

		dispatch(gameActions.incrementCash(amountToAddToCash));

		dispatch({ type: PRODUCTS_SOLD, value: amountToAddToCash });

		const gameUserId = gameSelectors.getGameId(getState());
		const result = await GameService.sellProduct(
			gameUserId,
			customer.id,
			customerIndex,
			productName, productCount, discount, 
			true,
			false,
		);

		if (!result.isSuccess) {
			console.warn("REQUEST FAILED: GameService.sellProduct");
			setLocalStore('screen', 'store');
			return false;
		}

		const notification = result.data;

		dispatch(inboxActions.handleNewMessage(notification));

		dispatch({
			type: types.CUSTOMERS_SELL_TO_CUSTOMER,
			customer: customer,
			customerIndex: customer?.avatarIndex
		});
		
		return true;
	};
}
